import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, HelpCircle, Check, Play, RotateCcw, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useChat } from "./ChatProvider";
import { useOnboarding } from "@/features/onboarding/OnboardingProvider";
import { allTours } from "@/features/onboarding/tours";
import type { TourDefinition } from "@/features/onboarding/types";
import type { ChatMessage } from "./types";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Unified chat + help widget. Sits at the bottom-right corner.
 * - Collapsed: a round button showing the message icon (with unread badge).
 * - Expanded: a chat panel with messages + input + tours sidebar.
 */
export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"chat" | "tours">("chat");
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSeenCountRef = useRef(0);

  const { messages, sending, sendMessage, loading: chatLoading } = useChat();
  const {
    currentTour,
    completed,
    dismissed,
    autoStart,
    loading: onboardingLoading,
    startTour,
    stopTour,
    resetTour,
    resetAll,
    setAutoStart,
    getTourForCurrentRoute,
  } = useOnboarding();

  const navigate = useNavigate();
  const location = useLocation();

  // Unread: any messages that arrived while the panel was closed
  const unreadCount = useMemo(() => {
    if (open) {
      lastSeenCountRef.current = messages.length;
      return 0;
    }
    return Math.max(0, messages.length - lastSeenCountRef.current);
  }, [messages.length, open]);

  // Track seen count when closing
  useEffect(() => {
    if (open) lastSeenCountRef.current = messages.length;
  }, [open, messages.length]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (open && tab === "chat") {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, open, tab]);

  // Focus input when opening chat tab
  useEffect(() => {
    if (open && tab === "chat") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, tab]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const body = input;
    setInput("");
    await sendMessage(body);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Tour helpers
  const pageTour = useMemo(() => getTourForCurrentRoute(), [getTourForCurrentRoute]);
  const pageTourStatus = useMemo(() => {
    if (!pageTour) return null;
    if (completed.has(pageTour.id)) return "completed" as const;
    if (dismissed.has(pageTour.id)) return "dismissed" as const;
    return "available" as const;
  }, [pageTour, completed, dismissed]);

  const hasUnseenTour = pageTourStatus === "available" || pageTourStatus === "dismissed";

  const launchTour = (tour: TourDefinition) => {
    const alreadyOnRoute = tour.routeMatch.test(location.pathname);
    resetTour(tour.id);
    if (!alreadyOnRoute && tour.path) {
      navigate(tour.path);
      setTimeout(() => startTour(tour.id), 500);
    } else {
      setTimeout(() => startTour(tour.id), 50);
    }
    setOpen(false);
  };

  // While a tour is running, show a "close tour" button
  if (currentTour) {
    return (
      <button
        data-tour="help-button"
        type="button"
        onClick={stopTour}
        aria-label="Close tour"
        className={cn(
          "fixed bottom-6 right-6 z-[10001] h-12 w-12 rounded-full shadow-lg",
          "flex items-center justify-center transition-all",
          "bg-background/90 backdrop-blur-sm border border-border text-foreground",
          "hover:bg-background hover:scale-105"
        )}
      >
        <X className="w-5 h-5" />
      </button>
    );
  }

  if (onboardingLoading || chatLoading) return null;

  return (
    <>
      {/* Collapsed button */}
      {!open && (
        <button
          data-tour="help-button"
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open help & chat"
          className={cn(
            "fixed bottom-6 right-6 z-[10001] h-12 w-12 rounded-full shadow-lg",
            "flex items-center justify-center transition-all",
            "gradient-primary text-primary-foreground hover:scale-105",
            "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background"
          )}
        >
          <MessageCircle className="w-5 h-5" />
          {(unreadCount > 0 || hasUnseenTour) && (
            <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-red-500 ring-2 ring-background text-[10px] font-bold text-white flex items-center justify-center">
              {unreadCount > 0 ? (unreadCount > 9 ? "9+" : unreadCount) : "!"}
            </span>
          )}
        </button>
      )}

      {/* Expanded panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[10001] w-96 h-[540px] rounded-2xl shadow-2xl border border-border bg-background flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
              <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-0.5">
                <button
                  onClick={() => setTab("chat")}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                    tab === "chat" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <MessageCircle className="w-3.5 h-3.5 inline mr-1" />
                  Chat
                </button>
                <button
                  onClick={() => setTab("tours")}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                    tab === "tours" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <HelpCircle className="w-3.5 h-3.5 inline mr-1" />
                  Tours
                </button>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>

            {/* Chat tab */}
            {tab === "chat" && (
              <>
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  {messages.length === 0 && (
                    <div className="text-center py-10">
                      <MessageCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No messages yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Send a message and we'll get back to you.</p>
                    </div>
                  )}
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-4 py-3 border-t border-border bg-card">
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      placeholder="Type a message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={sending}
                      className="flex-1 bg-secondary border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground"
                    />
                    <Button
                      size="icon"
                      className="h-9 w-9 gradient-primary text-primary-foreground shrink-0"
                      onClick={handleSend}
                      disabled={sending || !input.trim()}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Tours tab */}
            {tab === "tours" && (
              <div className="flex-1 overflow-y-auto">
                {pageTour && (
                  <div className="px-4 py-3 border-b border-border bg-secondary/30">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                          This page
                        </div>
                        <div className="text-sm font-medium truncate">{pageTour.label}</div>
                        <div className="text-xs text-muted-foreground leading-snug mt-0.5">
                          {pageTour.description}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="h-7 text-xs gradient-primary text-primary-foreground shrink-0"
                        onClick={() => launchTour(pageTour)}
                      >
                        {pageTourStatus === "completed" ? (
                          <><RotateCcw className="w-3 h-3 mr-1" /> Replay</>
                        ) : (
                          <><Play className="w-3 h-3 mr-1" /> Start</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="py-1">
                  <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    All tours · {completed.size}/{allTours.length} done
                  </div>
                  {allTours.map((t) => {
                    const isDone = completed.has(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => launchTour(t)}
                        className="w-full flex items-start gap-2 px-4 py-2 text-left hover:bg-secondary/60 transition-colors"
                      >
                        <div
                          className={cn(
                            "w-4 h-4 rounded-full border shrink-0 mt-0.5 flex items-center justify-center",
                            isDone ? "bg-primary border-primary" : "border-muted-foreground/40"
                          )}
                        >
                          {isDone && <Check className="w-3 h-3 text-primary-foreground" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{t.label}</div>
                          <div className="text-xs text-muted-foreground truncate">{t.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="border-t border-border px-4 py-3 space-y-3">
                  <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <div>
                      <div className="text-sm font-medium">Auto-start tours</div>
                      <div className="text-xs text-muted-foreground">Show me around new pages</div>
                    </div>
                    <Switch checked={autoStart} onCheckedChange={setAutoStart} />
                  </label>
                  {completed.size > 0 && (
                    <button
                      onClick={() => { if (confirm("Reset all tours? You'll see the onboarding again.")) resetAll(); }}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Reset all tours
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Message bubble ─────────────────────────────────────────────────────── */

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.sender_type === "user";
  const isSystem = message.sender_type === "system";

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-3.5 py-2",
          isUser
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : isSystem
            ? "bg-muted/60 text-muted-foreground rounded-bl-sm border border-border"
            : "bg-card border border-border text-foreground rounded-bl-sm"
        )}
      >
        {message.sender_type === "staff" && message.sender_name && (
          <p className="text-[10px] font-semibold text-primary mb-0.5">{message.sender_name}</p>
        )}
        {isSystem && (
          <p className="text-[10px] font-semibold text-muted-foreground mb-0.5">Zitiu</p>
        )}
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.body}</p>
        <p className={cn(
          "text-[10px] mt-1",
          isUser ? "text-primary-foreground/60" : "text-muted-foreground"
        )}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}
